import React, { useState } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, Users, Image, Heart, MessageSquare, Award
} from 'lucide-react';
import {
  useDziennikCompletionTrends,
  useDziennikCompletionAverage,
  useTeacherEngagement,
  useEvaluationScores,
  usePhotoStats,
  usePhotoReactionStats,
  useMessageStats,
  useAnalyticsGroups,
  useAnalyticsChildren
} from '@/hooks/useAnalytics';
import { useAuth } from '@/lib/AuthContext';

const COLORS = {
  complete: '#22c55e',
  incomplete: '#e2e8f0',
  positive: '#22c55e',
  neutral: '#f59e0b',
  negative: '#ef4444',
  photos: '#3b82f6',
  reactions: '#ec4899',
  messages: '#8b5cf6',
  announcements: '#06b6d4'
};

// Color palette for groups
const GROUP_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

export default function EngagementSection({ branchId, isMediaEnabled = true }) {
  const { userProfile } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);

  const isBranchAdmin = userProfile?.primary_role === 'branch_admin' ||
    userProfile?.active_role === 'branch_admin';

  // Data hooks
  const { data: groups = [] } = useAnalyticsGroups(branchId);
  const { data: children = [] } = useAnalyticsChildren(branchId, selectedGroup);

  const { data: trendsData, isLoading: trendsLoading } = useDziennikCompletionTrends(
    branchId, period, selectedGroup
  );
  const { data: avgData, isLoading: avgLoading } = useDziennikCompletionAverage(
    branchId, period, selectedGroup
  );
  const { data: teacherData, isLoading: teacherLoading } = useTeacherEngagement(
    branchId, selectedGroup
  );
  const { data: evalData, isLoading: evalLoading } = useEvaluationScores(
    branchId, period, selectedGroup, selectedChild
  );
  const { data: photoData, isLoading: photoLoading } = usePhotoStats(branchId, period);
  const { data: reactionData, isLoading: reactionLoading } = usePhotoReactionStats(branchId, period);
  const { data: messageData, isLoading: messageLoading } = useMessageStats(branchId, 'monthly');

  // Format date for chart
  const formatChartDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'd MMM', { locale: pl });
  };

  // Pie chart data for completion
  const completionPieData = avgData ? [
    { name: 'Wypełnione', value: avgData.complete, color: COLORS.complete },
    { name: 'Niewypełnione', value: avgData.incomplete, color: COLORS.incomplete }
  ].filter(item => item.value > 0) : [];

  // Transform evaluation data for stacked bar chart
  const evalChartData = evalData?.groups?.map(group => {
    const result = { groupName: group.groupName };
    Object.entries(group.criteria).forEach(([key, values]) => {
      result[`${key}_positive`] = values.positive;
      result[`${key}_neutral`] = values.neutral;
      result[`${key}_negative`] = values.negative;
    });
    return result;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Section Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Zaangażowanie</h2>

        <div className="flex flex-wrap items-center gap-2">
          {/* Period Toggle - Pill Slider */}
          <Tabs value={period} onValueChange={setPeriod} className="w-auto">
            <TabsList className="h-9 transition-all duration-300">
              <TabsTrigger value="weekly" className="text-xs px-3 transition-all duration-300">
                Tydzień
              </TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs px-3 transition-all duration-300">
                Miesiąc
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Select
            value={selectedGroup || 'all'}
            onValueChange={(v) => {
              setSelectedGroup(v === 'all' ? null : v);
              setSelectedChild(null);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Wszystkie grupy" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie grupy</SelectItem>
              {groups.map(group => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Dziennik Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dziennik Trends */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-slate-500" />
              Trendy uzupełnień dziennika
            </CardTitle>
            <CardDescription>
              Procent uzupełnionych wpisów dziennych
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendsLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendsData?.data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatChartDate}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0]?.payload;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                            <p className="font-medium text-slate-900 mb-1">
                              {format(new Date(label), 'EEEE, d MMMM', { locale: pl })}
                            </p>
                            <p className="text-sm text-green-600">
                              Wypełnione: {data?.complete} / {data?.expected}
                            </p>
                            <p className="text-sm text-slate-500">
                              ({data?.completionPercent}% kompletnych)
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="completionPercent"
                      name="Ukończone"
                      stroke={COLORS.complete}
                      strokeWidth={2}
                      dot={{ r: 4, fill: COLORS.complete }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dziennik Average Pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Średnia ukończenia</CardTitle>
            <CardDescription>
              {period === 'weekly' ? 'Ten tydzień' : 'Ten miesiąc'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {avgLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : completionPieData.length > 0 ? (
              <>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={completionPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={60}
                        dataKey="value"
                      >
                        {completionPieData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-2">
                  <p className="text-2xl font-bold text-green-600">
                    {avgData?.completionPercent || 0}%
                  </p>
                  <p className="text-sm text-slate-500">
                    {avgData?.complete} / {avgData?.total} wpisów
                  </p>
                </div>
              </>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                Brak danych
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Teacher Comparison (Branch Admin Only) */}
      {isBranchAdmin && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              Porównanie nauczycieli
              <Badge variant="outline" className="ml-2 text-xs">Tylko dla admina</Badge>
            </CardTitle>
            <CardDescription>
              Procent uzupełnionych wpisów dziennika na nauczyciela
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teacherLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : teacherData?.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={teacherData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0]?.payload;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                            <p className="font-medium">{data.name}</p>
                            <p className="text-sm text-green-600">
                              {data.completionPercent}% ({data.complete}/{data.total})
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="completionPercent"
                      fill={COLORS.complete}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                Brak danych nauczycieli
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Evaluation Scores */}
      {evalData?.criteria?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="w-4 h-4 text-slate-500" />
              Oceny według grup
            </CardTitle>
            <CardDescription>
              Rozkład ocen (pozytywne / neutralne / negatywne) na grupę
            </CardDescription>
            {selectedGroup && children.length > 0 && (
              <Select
                value={selectedChild || 'all'}
                onValueChange={(v) => setSelectedChild(v === 'all' ? null : v)}
              >
                <SelectTrigger className="w-48 mt-2">
                  <SelectValue placeholder="Wszystkie dzieci" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie dzieci</SelectItem>
                  {children.map(child => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.first_name} {child.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardHeader>
          <CardContent>
            {evalLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : evalData?.groups?.length > 0 ? (
              <div className="space-y-4">
                {evalData.groups.map((group, groupIndex) => (
                  <div key={group.groupId} className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">{group.groupName}</h4>
                    <div className="space-y-1">
                      {Object.entries(group.criteria).map(([key, values]) => {
                        const total = values.total || 1;
                        const posPercent = Math.round((values.positive / total) * 100);
                        const neutPercent = Math.round((values.neutral / total) * 100);
                        const negPercent = Math.round((values.negative / total) * 100);

                        return (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 w-32 truncate">
                              {values.name}
                            </span>
                            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden flex">
                              {posPercent > 0 && (
                                <div
                                  className="h-full bg-green-500"
                                  style={{ width: `${posPercent}%` }}
                                  title={`Pozytywne: ${posPercent}%`}
                                />
                              )}
                              {neutPercent > 0 && (
                                <div
                                  className="h-full bg-amber-400"
                                  style={{ width: `${neutPercent}%` }}
                                  title={`Neutralne: ${neutPercent}%`}
                                />
                              )}
                              {negPercent > 0 && (
                                <div
                                  className="h-full bg-red-500"
                                  style={{ width: `${negPercent}%` }}
                                  title={`Negatywne: ${negPercent}%`}
                                />
                              )}
                            </div>
                            <span className="text-xs text-slate-400 w-12 text-right">
                              {total}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {/* Legend */}
                <div className="flex items-center justify-center gap-4 pt-2 border-t">
                  <div className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded bg-green-500" />
                    <span className="text-slate-600">Pozytywne</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded bg-amber-400" />
                    <span className="text-slate-600">Neutralne</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded bg-red-500" />
                    <span className="text-slate-600">Negatywne</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                Brak danych o ocenach
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photos & Reactions Row - hidden when Media module disabled */}
      {isMediaEnabled && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* New Photos */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Image className="w-4 h-4 text-slate-500" />
              Nowe zdjęcia
            </CardTitle>
            <CardDescription>
              Liczba zdjęć dodanych {period === 'weekly' ? 'w tym tygodniu' : 'w tym miesiącu'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {photoLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : photoData?.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={photoData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="groupName"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                            <p className="font-medium">{payload[0]?.payload?.groupName}</p>
                            <p className="text-sm text-blue-600">
                              {payload[0]?.value} zdjęć
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill={COLORS.photos}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                Brak nowych zdjęć
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Reactions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="w-4 h-4 text-slate-500" />
              Nowe reakcje rodziców
            </CardTitle>
            <CardDescription>
              Reakcje na zdjęcia {period === 'weekly' ? 'w tym tygodniu' : 'w tym miesiącu'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reactionLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : reactionData?.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={reactionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="groupName"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                            <p className="font-medium">{payload[0]?.payload?.groupName}</p>
                            <p className="text-sm text-pink-600">
                              {payload[0]?.value} reakcji
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill={COLORS.reactions}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                Brak nowych reakcji
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Messages */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-500" />
            Komunikacja miesięczna
          </CardTitle>
          <CardDescription>
            Ogłoszenia i wiadomości z bieżącego miesiąca
          </CardDescription>
        </CardHeader>
        <CardContent>
          {messageLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : messageData?.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={messageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="groupName"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0]?.payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                          <p className="font-medium">{data?.groupName}</p>
                          <p className="text-sm text-cyan-600">
                            Ogłoszenia: {data?.announcements}
                          </p>
                          <p className="text-sm text-purple-600">
                            Wiadomości: {data?.messages}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    content={() => (
                      <div className="flex justify-center gap-4 pt-2">
                        <div className="flex items-center gap-1 text-xs">
                          <div className="w-3 h-3 rounded" style={{ background: COLORS.announcements }} />
                          <span>Ogłoszenia</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <div className="w-3 h-3 rounded" style={{ background: COLORS.messages }} />
                          <span>Wiadomości</span>
                        </div>
                      </div>
                    )}
                  />
                  <Bar
                    dataKey="announcements"
                    stackId="a"
                    fill={COLORS.announcements}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="messages"
                    stackId="a"
                    fill={COLORS.messages}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
              Brak danych o komunikacji
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
